import { Children, cloneElement, useCallback, useEffect } from 'react';

import { playSound, Sound } from '~/assets/sounds';
import { useInputAction } from '~/rage';
import { InputAction } from '~/shared';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { setTabIndex, setTabsCount } from '../../redux/slices/vehicleTabsSlice';
import { TabLine } from '../TabLine';
import { StyledContainer } from './TabsContainer.styled';

const TabsContainer = ({
	children,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => {
	const tabIndex = useLayoutSelector((state) => state.vehicleTabs.tabIndex);
	const dispatch = useLayoutDispatch();

	useEffect(() => {
		const tabsCount = Children.count(children);
		dispatch(setTabsCount(tabsCount));
	}, [children]);

	const handleTabShift = useCallback(
		(amount: number) => {
			dispatch(setTabIndex(tabIndex + amount));
		},
		[tabIndex],
	);
	useInputAction(InputAction.CommonTabNavLeft, () => {
		handleTabShift(-1);
	});
	useInputAction(InputAction.CommonTabNavRight, () => {
		handleTabShift(1);
	});

	useEffect(() => {
		const tabElement = document.getElementById(`tab#${tabIndex}`);
		if (tabElement !== null) {
			tabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [tabIndex]);

	useEffect(() => {
		const tabElement = document.getElementById(`tab#${tabIndex}`);
		const lineElement = document.getElementById(`tab#line`);
		if (tabElement !== null && lineElement !== null) {
			const tabWidth = window.getComputedStyle(tabElement).width;
			lineElement.style.setProperty('--width', tabWidth);
			lineElement.style.setProperty('--left', `${tabElement.offsetLeft}px`);
		}
	}, [tabIndex]);

	return (
		<StyledContainer {...props}>
			{Children.map(children, (child: any, index) => {
				return cloneElement(child, {
					id: `tab#${index}`,
					onClick: () => {
						playSound(Sound.ClickButton);
						dispatch(setTabIndex(index));
					},
					$selected: index == tabIndex,
					onMouseEnter: () => playSound(Sound.LinkCursor),
				});
			})}
			<TabLine id="tab#line" />
		</StyledContainer>
	);
};

export { TabsContainer };
